import React, { useState, useEffect } from 'react';
import { getThumbnail } from '../utils/thumbnailService';

interface ArticleThumbnailProps {
  articleId: string;
  emojiThumbnail: string;
  className?: string;
  alt?: string;
}

const ArticleThumbnail: React.FC<ArticleThumbnailProps> = ({
  articleId,
  emojiThumbnail,
  className = '',
  alt = 'Article thumbnail'
}) => {
  const [thumbnailInfo, setThumbnailInfo] = useState<{
    type: 'image' | 'emoji';
    src: string;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadThumbnail = async () => {
      try {
        setIsLoading(true);
        const info = await getThumbnail(articleId, emojiThumbnail);
        setThumbnailInfo(info);
      } catch (error) {
        console.error(`Error loading thumbnail for ${articleId}:`, error);
        // Fallback to emoji
        setThumbnailInfo({
          type: 'emoji',
          src: emojiThumbnail
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadThumbnail();
  }, [articleId, emojiThumbnail]);

  // Show loading state
  if (isLoading) {
    return (
      <div className={`articles__card-thumbnail articles__card-thumbnail--loading ${className}`}>
        <span>⏳</span>
      </div>
    );
  }

  // Show fallback if no thumbnail info
  if (!thumbnailInfo) {
    return (
      <div className={`articles__card-thumbnail articles__card-thumbnail--error ${className}`}>
        <span>{emojiThumbnail}</span>
      </div>
    );
  }

  // Render image thumbnail
  if (thumbnailInfo.type === 'image') {
    return (
      <div 
        className={`articles__card-thumbnail articles__card-thumbnail--image ${className}`}
        role="img"
        aria-label={alt}
      >
        <img
          src={thumbnailInfo.src}
          alt={alt}
          onError={() => {
            // Fallback to emoji on error
            setThumbnailInfo({
              type: 'emoji',
              src: emojiThumbnail
            });
          }}
        />
      </div>
    );
  }

  // Render emoji thumbnail
  return (
    <div className={`articles__card-thumbnail articles__card-thumbnail--emoji ${className}`}>
      <span>{thumbnailInfo.src}</span>
    </div>
  );
};

export default ArticleThumbnail; 